import zenkyEvents from '@/events'

function quantity(line) {
  const value = Number(line?.quantity || 0)

  return Number.isFinite(value) ? value : 0
}

function compactOrderLine(line) {
  return {
    uuid: line.uuid,
    product_variant_id: line.product_variant_id,
    promotion_id: line.promotion_id,
    promotion_reward_id: line.promotion_reward_id,
    stock_id: line.stock_id,
    modifiers_hash: line.modifiers_hash,
    is_promocode_reward: line.is_promocode_reward,
    quantity: line.quantity,
    unit_price: line.unit_price,
    total_price: line.total_price,
    product: line.product,
    variant: line.variant,
  }
}

export default class CommerceEventsTracker {
  constructor(app, client) {
    this.app = app
    this.client = client
    this.store = app.$store
    this.router = app.$router
    this.installed = false
    this.recentEvents = new Map()
    this.actionSnapshots = {}
    this.unsubscribers = []
    this.cartOrderWatcher = null
  }

  install() {
    if (this.installed) {
      return
    }

    this.installed = true
    this.trackPageViewed(this.router.currentRoute, null)
    this.trackCategoryViewed(this.router.currentRoute)
    this.installRouterTracking()
    this.installProductTracking()
    this.installOrderActionTracking()
    this.installCartTracking()
    this.installCategoryRetryTracking()
  }

  destroy() {
    this.unsubscribers.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    })

    if (this.cartOrderWatcher) {
      this.cartOrderWatcher()
      this.cartOrderWatcher = null
    }

    this.client.destroy()
  }

  installRouterTracking() {
    const unregister = this.router.afterEach((to, from) => {
      this.safeRun(() => {
        this.trackPageViewed(to, from)
        this.trackCategoryViewed(to)
      })
    })

    if (typeof unregister === 'function') {
      this.unsubscribers.push(unregister)
    }
  }

  installProductTracking() {
    this.app.sharedEventBus.$on(zenkyEvents.PRODUCT_OPENED, payload => {
      this.safeRun(() => this.trackProductViewed(payload))
    })
  }

  installOrderActionTracking() {
    const unsubscribe = this.store.subscribeAction({
      before: action => {
        if (!['order/addProductVariant', 'order/removeProductVariant'].includes(action.type)) {
          return
        }

        this.pushActionSnapshot(action.type, this.takeOrderSnapshot())
      },

      after: action => {
        if (action.type === 'order/addProductVariant') {
          this.safeRun(() => this.trackProductAddedToCart(action.payload, this.shiftActionSnapshot(action.type)))
        } else if (action.type === 'order/removeProductVariant') {
          this.safeRun(() => this.trackProductRemovedFromCart(action.payload, this.shiftActionSnapshot(action.type)))
        }
      },

      error: action => {
        if (['order/addProductVariant', 'order/removeProductVariant'].includes(action.type)) {
          this.shiftActionSnapshot(action.type)
        }
      },
    })

    this.unsubscribers.push(unsubscribe)
  }

  installCartTracking() {
    const unwatch = this.app.$watch(
      () => this.store.state.ui.cartModalIsActive,
      (isActive, wasActive) => {
        if (isActive && !wasActive) {
          this.safeRun(() => this.trackCartViewedOrWaitForOrder())
        }
      },
    )

    this.unsubscribers.push(unwatch)
  }

  installCategoryRetryTracking() {
    const unsubscribe = this.store.subscribe(mutation => {
      if (mutation.type === 'catalog/setVisibleCategoriesTree') {
        this.safeRun(() => this.trackCategoryViewed(this.router.currentRoute))
      }
    })

    this.unsubscribers.push(unsubscribe)
  }

  trackPageViewed(to, from) {
    if (!to || !to.path) {
      return
    }

    this.client.track('page_viewed', {
      context: this.buildRouteContext(to, from),
      properties: {
        page_type: to.name || 'unknown',
      },
    })
  }

  trackCategoryViewed(route) {
    if (!route || route.name !== 'catalog' || !route.params?.categoryId) {
      return
    }

    const category = this.resolveCategory(route.params.categoryId)

    if (!category || !category.id) {
      return
    }

    const key = `category_viewed:${category.id}:${route.path}`

    if (this.skipRecent(key)) {
      return
    }

    this.client.track('category_viewed', {
      context: this.buildRouteContext(route),
      properties: {
        category_id: category.id,
      },
    })
  }

  trackProductViewed(payload = {}) {
    const product = payload.product

    if (!product || !product.id) {
      return
    }

    const route = this.router.currentRoute
    const key = `product_viewed:${product.id}:${route?.path || ''}`

    if (this.skipRecent(key)) {
      return
    }

    const properties = {
      product_id: product.id,
      category_id: product.categories?.[0]?.id,
      product_variant_id: this.resolveViewedProductVariantId(product),
    }

    this.client.track('product_viewed', {
      context: this.buildRouteContext(route),
      properties,
    })
  }

  trackProductAddedToCart(payload = {}, snapshot) {
    const current = this.takeOrderSnapshot()
    const change = this.findChangedLine(snapshot?.variants || [], current.variants, payload, 'add')
    const line = change?.afterLine

    if (!current.orderId || !line || !line.product?.id) {
      return
    }

    const variantId = this.getLineVariantId(line) || payload.variantId

    if (!variantId) {
      return
    }

    const properties = {
      product_id: line.product.id,
      product_variant_id: variantId,
      quantity: change.quantity || Number(payload.quantity || 1),
      unit_price: this.client.formatPrice(line.unit_price || line.variant?.price),
      total_price: this.formatLineQuantityTotalPrice(line, change.quantity),
    }

    this.client.track('product_added_to_cart', {
      orderId: current.orderId,
      context: this.buildRouteContext(this.router.currentRoute),
      properties,
    })
  }

  trackProductRemovedFromCart(payload = {}, snapshot) {
    const current = this.takeOrderSnapshot()
    const change = this.findChangedLine(snapshot?.variants || [], current.variants, payload, 'remove')
    const line = change?.beforeLine

    if (!snapshot?.orderId || !line || !line.product?.id) {
      return
    }

    const variantId = this.getLineVariantId(line) || payload.variantId

    if (!variantId) {
      return
    }

    const properties = {
      product_id: line.product.id,
      product_variant_id: variantId,
      quantity: change.quantity || Number(payload.quantity || 1),
    }

    this.client.track('product_removed_from_cart', {
      orderId: snapshot.orderId,
      context: this.buildRouteContext(this.router.currentRoute),
      properties,
    })
  }

  trackCartViewedOrWaitForOrder() {
    const order = this.store.state.order.order

    if (order?.id) {
      this.trackCartViewed(order)
      return
    }

    if (this.cartOrderWatcher) {
      return
    }

    this.cartOrderWatcher = this.app.$watch(
      () => this.store.state.order.order?.id,
      orderId => {
        if (!orderId || !this.store.state.ui.cartModalIsActive) {
          return
        }

        if (this.cartOrderWatcher) {
          this.cartOrderWatcher()
          this.cartOrderWatcher = null
        }

        this.safeRun(() => this.trackCartViewed(this.store.state.order.order))
      },
    )
  }

  trackCartViewed(order) {
    if (!order?.id) {
      return
    }

    const key = `cart_viewed:${order.id}`

    if (this.skipRecent(key)) {
      return
    }

    this.client.track('cart_viewed', {
      orderId: order.id,
      context: this.buildRouteContext(this.router.currentRoute),
      properties: this.buildOrderSummaryProperties(order),
    })
  }

  trackCheckoutStartedAndFlush(order, options = {}) {
    try {
      if (!order?.id) {
        return Promise.resolve(false)
      }

      return this.client.trackAndFlush('checkout_started', {
        orderId: order.id,
        context: this.buildRouteContext(this.router.currentRoute),
        properties: this.buildOrderSummaryProperties(order),
      }, {
        timeoutMs: options.timeoutMs || 500,
      })
    } catch (e) {
      return Promise.resolve(false)
    }
  }

  getIdentifiers() {
    return this.client.getIdentifiers()
  }

  buildOrderSummaryProperties(order) {
    const variants = Array.isArray(order.variants) ? order.variants : []

    return {
      products_count: variants.length,
      products_quantity: this.store.getters['order/totalQuantity'],
      total_price: this.client.formatPrice(order.total_price),
    }
  }

  buildRouteContext(route, from = null) {
    const context = {
      path: route?.path || '/',
      platform: 'web',
    }

    const cityId = this.store.getters['store/activeCityId']

    if (cityId) {
      context.city_id = cityId
    }

    if (from?.path) {
      context.referrer_path = from.path
    } else {
      const referrer = this.getExternalReferrer()

      if (referrer) {
        context.referrer_path = referrer.path
        context.referrer_host = referrer.host
      }
    }

    const query = route?.query || {}
    ;['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(field => {
      if (query[field]) {
        context[field] = query[field]
      }
    })

    return context
  }

  getExternalReferrer() {
    if (typeof document === 'undefined' || !document.referrer) {
      return null
    }

    try {
      const url = new URL(document.referrer)

      return {
        path: url.pathname,
        host: url.host,
      }
    } catch (e) {
      return null
    }
  }

  resolveCategory(categoryId) {
    const categories = this.store.getters['catalog/flatVisibleCategories'] || []

    return categories.find(category => category.short_id === categoryId || category.id === categoryId)
  }

  resolveViewedProductVariantId(product) {
    const variants = Array.isArray(product.variants) ? product.variants : []
    const routeVariantId = this.router.currentRoute?.query?.variant

    if (routeVariantId && variants.some(variant => variant.id === routeVariantId)) {
      return routeVariantId
    }

    if (product.single_variant === true && variants[0]?.id) {
      return variants[0].id
    }

    if (variants.length === 1 && variants[0]?.id) {
      return variants[0].id
    }

    return undefined
  }

  takeOrderSnapshot() {
    const order = this.store.state.order.order || {}
    const variants = Array.isArray(order.variants) ? order.variants : []

    return {
      orderId: order.id || '',
      variants: variants.map(variant => compactOrderLine(variant)),
    }
  }

  pushActionSnapshot(type, snapshot) {
    if (!Array.isArray(this.actionSnapshots[type])) {
      this.actionSnapshots[type] = []
    }

    this.actionSnapshots[type].push(snapshot)
  }

  shiftActionSnapshot(type) {
    if (!Array.isArray(this.actionSnapshots[type])) {
      return null
    }

    return this.actionSnapshots[type].shift() || null
  }

  findChangedLine(beforeVariants, afterVariants, payload, type) {
    if (type === 'add') {
      return this.findAddedLine(beforeVariants, afterVariants, payload)
    }

    return this.findRemovedLine(beforeVariants, afterVariants, payload)
  }

  findAddedLine(beforeVariants, afterVariants, payload) {
    const candidates = afterVariants.filter(line => this.lineMatchesPayload(line, payload))

    for (const afterLine of candidates) {
      const beforeLine = this.findMatchingLine(beforeVariants, afterLine)
      const delta = quantity(afterLine) - quantity(beforeLine)

      if (delta > 0) {
        return { beforeLine, afterLine, quantity: delta }
      }
    }

    if (candidates[0]) {
      return { beforeLine: null, afterLine: candidates[0], quantity: Number(payload.quantity || 1) }
    }

    return null
  }

  findRemovedLine(beforeVariants, afterVariants, payload) {
    const candidates = beforeVariants.filter(line => this.lineMatchesPayload(line, payload))

    for (const beforeLine of candidates) {
      const afterLine = this.findMatchingLine(afterVariants, beforeLine)
      const delta = quantity(beforeLine) - quantity(afterLine)

      if (delta > 0) {
        return { beforeLine, afterLine, quantity: delta }
      }
    }

    if (candidates[0]) {
      return { beforeLine: candidates[0], afterLine: null, quantity: Number(payload.quantity || 1) }
    }

    return null
  }

  findMatchingLine(lines, target) {
    if (!target) {
      return null
    }

    const byUuid = lines.find(line => line.uuid && line.uuid === target.uuid)

    if (byUuid) {
      return byUuid
    }

    return lines.find(line => (
      this.getLineVariantId(line) === this.getLineVariantId(target)
      && line.promotion_id === target.promotion_id
      && line.promotion_reward_id === target.promotion_reward_id
      && line.stock_id === target.stock_id
      && line.modifiers_hash === target.modifiers_hash
      && line.is_promocode_reward === target.is_promocode_reward
    )) || null
  }

  lineMatchesPayload(line, payload = {}) {
    if (!line) {
      return false
    }

    if (payload.variantId && this.getLineVariantId(line) !== payload.variantId) {
      return false
    }

    if (payload.promotion_id && line.promotion_id !== payload.promotion_id) {
      return false
    }

    if (payload.promotion_reward_id && line.promotion_reward_id !== payload.promotion_reward_id) {
      return false
    }

    if (payload.stock_id && line.stock_id !== payload.stock_id) {
      return false
    }

    return true
  }

  getLineVariantId(line) {
    return line?.product_variant_id || line?.variant?.id || ''
  }

  formatLineQuantityTotalPrice(line, changedQuantity) {
    const unitPrice = line.unit_price || line.variant?.price
    const minorValue = this.client.getPriceMinorValue(unitPrice)

    if (minorValue !== null && changedQuantity) {
      return this.client.formatMinorPriceValue(minorValue * changedQuantity)
    }

    if (quantity(line) === changedQuantity) {
      return this.client.formatPrice(line.total_price)
    }

    return undefined
  }

  skipRecent(key, ttl = 1000) {
    const now = Date.now()
    const recentAt = this.recentEvents.get(key)

    if (recentAt && now - recentAt < ttl) {
      return true
    }

    this.recentEvents.set(key, now)

    for (const [recentKey, timestamp] of this.recentEvents.entries()) {
      if (now - timestamp > ttl) {
        this.recentEvents.delete(recentKey)
      }
    }

    return false
  }

  safeRun(callback) {
    try {
      callback()
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[CommerceEvents] Tracking hook failed.', e)
      }
    }
  }
}
